
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://mxfbnsxwxckqjsbuptmf.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im14ZmJuc3h3eGNrcWpzYnVwdG1mIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDYzNTAyMjIsImV4cCI6MjA2MTkyNjIyMn0.rH4IN9YFzn6eUhcPR9Mfbj1GoBz-_8cB6ZLAaoctsW4";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

console.log('Initializing Supabase client with:', { 
  url: SUPABASE_URL, 
  keyAvailable: !!SUPABASE_PUBLISHABLE_KEY
});

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'X-Client-Info': '3DPHub-WebApp'
    }
  }
});

// Function to check Supabase connection
export const checkSupabaseConnection = async (): Promise<boolean> => {
  try {
    console.log('Testing Supabase connection...');
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Connection timeout')), 10000)
    );
    
    // Simple query to check connection
    const queryPromise = supabase.from('parts').select('id').limit(1);
    
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
    
    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }
    
    console.log('Supabase connection successful. Sample data:', data);
    return true;
  } catch (error) {
    console.error('Exception when checking Supabase connection:', error);
    return false;
  }
};

// Enhanced query wrapper with error handling and retries
export const createSafeQuery = <T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  options: {
    retries?: number;
    retryDelay?: number;
    fallbackData?: T;
    context?: string;
  } = {}
) => {
  const { retries = 2, retryDelay = 1000, fallbackData = null, context = 'query' } = options;
  
  return async (): Promise<{ data: T | null; error: any; success: boolean }> => {
    let lastError: any;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const result = await queryFn();
        
        if (result.error) {
          lastError = result.error;
          
          // Don't retry on authentication or permission errors
          if (result.error.code === 'PGRST301' || result.error.code === '42501') {
            break;
          }
          
          if (attempt < retries) {
            console.warn(`${context} attempt ${attempt + 1} failed, retrying...`, result.error);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
        }
        
        return {
          data: result.data || fallbackData,
          error: result.error,
          success: !result.error
        };
        
      } catch (error) {
        lastError = error;
        
        if (attempt < retries) {
          console.warn(`${context} attempt ${attempt + 1} failed, retrying...`, error);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
      }
    }
    
    console.error(`${context} failed after ${retries + 1} attempts:`, lastError);
    
    return {
      data: fallbackData,
      error: lastError,
      success: false
    };
  };
};

// Connection monitoring
let connectionStatus = true;
let connectionListeners: ((status: boolean) => void)[] = [];

export const onConnectionChange = (callback: (status: boolean) => void) => {
  connectionListeners.push(callback);
  return () => {
    connectionListeners = connectionListeners.filter(cb => cb !== callback);
  };
};

export const getConnectionStatus = () => connectionStatus;

// Periodic connection check
setInterval(async () => {
  const newStatus = await checkSupabaseConnection();
  if (newStatus !== connectionStatus) {
    connectionStatus = newStatus;
    connectionListeners.forEach(cb => cb(newStatus));
  }
}, 60000); // Check every minute
